import React, { useEffect } from 'react';
import { Routes, Route, Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  CssBaseline,
  Box,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import { Home as HomeIcon, Book as BookIcon, Add as AddIcon, Logout as LogoutIcon, AutoStories as StoriesIcon } from '@mui/icons-material';
import BookSearch from './components/BookSearch';
import BookList from './components/BookList';
import BookDetails from './components/BookDetails';
import Home from './components/Home';
import Login from './components/Login';
import StoriesList from './components/StoriesList';
import StoryGenreSelector from './components/StoryGenreSelector';
import InteractiveStory from './components/InteractiveStory';
import GlobalChatbot, { ChatbotFAB } from './components/GlobalChatbot';
import { ChatbotProvider, useChatbotActions } from './contexts/ChatbotContext';
import { styled, useTheme } from '@mui/material/styles';
import LogoImage from './components/Logo.png'; // Adjust the path as needed

// Styled Components
const Logo = styled('img')(({ theme }) => ({
  width: '80px', // Reduced from 160px to 120px
  height: 'auto',
  [theme.breakpoints.down('sm')]: {
    width: '60px', // Reduced from 120px to 100px
  },
}));

// Inner App component that uses chatbot context
function AppContent() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { resetConversation, updateCurrentPage } = useChatbotActions();

  useEffect(() => {
    const token = localStorage.getItem('token');
    // Only redirect if not on login page and no token exists
    if (!token && location.pathname !== '/') {
      navigate('/');
    }
  }, [navigate, location]);

  // Track current page for chatbot context
  useEffect(() => {
    const path = location.pathname;
    let pageName = 'Unknown';
    let context = {};

    if (path === '/') {
      pageName = 'Login';
      context = {
        description: 'User is on the login page - not yet authenticated. No book data available yet.',
        capabilities: []
      };
    } else if (path === '/home') {
      pageName = 'Home';
      context = {
        description: 'User is viewing their home dashboard with reading statistics, active reading challenges, and progress tracking.',
        capabilities: [
          'View overall reading statistics (books read, currently reading, want to read)',
          'See active reading challenges and their progress',
          'Access reading journey insights and analytics',
          'Quick access to recently added or updated books'
        ]
      };
    } else if (path === '/my-books') {
      pageName = 'My Books';
      context = {
        description: 'User is browsing their complete book collection with filtering and search capabilities.',
        capabilities: [
          'Filter books by status (finished, currently reading, want to read, dropped)',
          'Search books by title or author',
          'View all books in the collection',
          'Quick access to individual book details'
        ]
      };
    } else if (path === '/search') {
      pageName = 'Add New Book';
      context = {
        description: 'User is searching for new books to add to their collection using the Google Books API.',
        capabilities: [
          'Search for books by title, author, or keywords',
          'Preview book details from Google Books',
          'Add new books to the collection',
          'Set initial status when adding books'
        ]
      };
    } else if (path.startsWith('/book/')) {
      pageName = 'Book Details';
      context = {
        description: 'User is viewing detailed information about a specific book, including reviews, logs, and status.',
        capabilities: [
          'View book description, authors, and metadata',
          'Update reading status',
          'Rate the book',
          'Write or edit reviews',
          'Add reading log entries',
          'Delete the book from collection'
        ]
      };
    } else if (path === '/stories') {
      pageName = 'Royal Tales';
      context = {
        description: 'User is viewing their collection of interactive choose-your-own-adventure stories.',
        capabilities: [
          'View active and completed stories',
          'Continue reading active stories',
          'Start new interactive tales',
          'Delete stories from collection'
        ]
      };
    } else if (path === '/story/new') {
      pageName = 'New Tale';
      context = {
        description: 'User is selecting a genre to begin a new interactive story adventure.',
        capabilities: [
          'Choose from Fantasy, Mystery, Adventure, Romance, Horror, or Fairy Tale genres',
          'Start a new choose-your-own-adventure story'
        ]
      };
    } else if (path.startsWith('/story/')) {
      pageName = 'Interactive Story';
      context = {
        description: 'User is reading an interactive story and making choices that shape the narrative.',
        capabilities: [
          'Read the current chapter with AI-generated illustrations',
          'Make choices that determine the story path',
          'Track story progress and completion',
          'Delete the story if desired'
        ]
      };
    }

    updateCurrentPage(path, pageName, context);
  }, [location, updateCurrentPage]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('chatbot_history'); // Clear chat history on logout
    resetConversation();
    navigate('/');
  };

  const isLoginPage = location.pathname === '/';

  return (
    <>
      <CssBaseline />
      {!isLoginPage && (
      <AppBar
        position={isMobile ? 'fixed' : 'static'}
        sx={{
          backgroundColor: '#FAF3E0',
          color: '#333',
          boxShadow: 'none',
          top: isMobile ? 'auto' : 0,
          bottom: isMobile ? 0 : 'auto',
        }}
      >
        <Toolbar sx={{ justifyContent: isMobile ? 'space-around' : 'flex-start' }}>
          {!isMobile ? (
            // Replace Typography with Logo
            <Box sx={{ flexGrow: 1 }}>
              <RouterLink to="/home" style={{ textDecoration: 'none' }}>
                <Logo src={LogoImage} alt="Logo" />
              </RouterLink>
            </Box>
          ) : (
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#8D7B68' }}>
              Books
            </Typography>
          )}
          {isMobile ? (
            <>
              <IconButton component={RouterLink} to="/home" sx={{ color: '#333' }}>
                <HomeIcon />
              </IconButton>
              <IconButton component={RouterLink} to="/my-books" sx={{ color: '#333' }}>
                <BookIcon />
              </IconButton>
              <IconButton component={RouterLink} to="/stories" sx={{ color: '#333' }}>
                <StoriesIcon />
              </IconButton>
              <IconButton component={RouterLink} to="/search" sx={{ color: '#333' }}>
                <AddIcon />
              </IconButton>
              <IconButton onClick={handleLogout} sx={{ color: '#d32f2f' }}>
                <LogoutIcon />
              </IconButton>
            </>
          ) : (
            <>
              <Button
                component={RouterLink}
                to="/home"
                sx={{
                  color: '#333',
                  '&:hover': { backgroundColor: '#F4ECE3' },
                  borderRadius: '20px',
                  px: 2,
                }}
              >
                Home
              </Button>
              <Button
                component={RouterLink}
                to="/my-books"
                sx={{
                  color: '#333',
                  '&:hover': { backgroundColor: '#F4ECE3' },
                  borderRadius: '20px',
                  px: 2,
                }}
              >
                My Books
              </Button>
              <Button
                component={RouterLink}
                to="/stories"
                sx={{
                  color: '#333',
                  '&:hover': { backgroundColor: '#F4ECE3' },
                  borderRadius: '20px',
                  px: 2,
                }}
              >
                Royal Tales
              </Button>
              <Button
                component={RouterLink}
                to="/search"
                sx={{
                  color: '#333',
                  '&:hover': { backgroundColor: '#F4ECE3' },
                  borderRadius: '20px',
                  px: 2,
                }}
              >
                Add New Book
              </Button>
              <Button
                onClick={handleLogout}
                sx={{
                  color: '#d32f2f',
                  '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' },
                  borderRadius: '20px',
                  px: 2,
                  ml: 'auto',
                }}
              >
                Logout
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>
      )}

      <Box
        sx={{
          backgroundColor: '#FAF3E0',
          minHeight: '100vh',
          pt: isLoginPage ? 0 : (isMobile ? 3 : 9),
          pb: isLoginPage ? 0 : (isMobile ? 9 : 3),
          px: isLoginPage ? 0 : { xs: 2, sm: 4, md: 6 },
        }}
      >
        <Routes>
          <Route path="/" element={<Login />} />
          <Route path="/home" element={<Home />} />
          <Route path="/my-books" element={<BookList />} />
          <Route path="/stories" element={<StoriesList />} />
          <Route path="/story/new" element={<StoryGenreSelector />} />
          <Route path="/story/:id" element={<InteractiveStory />} />
          <Route path="/search" element={<BookSearch />} />
          <Route path="/book/:id" element={<BookDetails />} />
        </Routes>
      </Box>

      {/* Global Chatbot Components */}
      {!isLoginPage && (
        <>
          <GlobalChatbot />
          <ChatbotFAB />
        </>
      )}
    </>
  );
}

// Main App wrapper with ChatbotProvider
function App() {
  return (
    <ChatbotProvider>
      <AppContent />
    </ChatbotProvider>
  );
}

export default App;
